import { z } from 'zod';
import { dateTimeSchema, extraValuesSchema, idSchema } from './common';
import { csvDelimiterSchema, detectedEncodingSchema } from './enums';
import { batchSummarySchema } from './batches';

/** SPEC §5 — two-step CSV import: preview then commit. */

export const REQUIRED_HEADERS = ['english_text', 'arabic_text'] as const;
export const PREVIEW_TTL_MINUTES = 15;
export const PREVIEW_SAMPLE_ROWS = 5;

/** Whole-file reject reasons (SPEC §5 "Validation"). */
export const IMPORT_ERROR_CODES = [
  'missing_required_header',
  /**
   * Two columns share a header name (including two blank ones, the usual Excel
   * trailing-comma artefact). `Row.extraValues` is keyed by header name, so the later
   * column would silently overwrite the earlier one and the export would print the same
   * value twice — a passthrough column rewritten with another column's data.
   */
  'duplicate_header',
  'blank_english_text',
  'cell_too_long',
  'too_many_rows',
  'invalid_encoding',
  'replacement_character',
  'inconsistent_column_count',
  'empty_file',
  'parse_error',
] as const;
export const importErrorCodeSchema = z.enum(IMPORT_ERROR_CODES);
export type ImportErrorCode = z.infer<typeof importErrorCodeSchema>;

export const importErrorSchema = z.object({
  code: importErrorCodeSchema,
  /** 1-based line number in the source file; null for file-level errors. */
  line: z.number().int().min(1).nullable(),
  column: z.string().nullable(),
  message: z.string(),
});
export type ImportError = z.infer<typeof importErrorSchema>;

/** Non-blocking warnings (SPEC §5 "Warnings"). */
export const IMPORT_WARNING_CODES = [
  'duplicate_english_text',
  'digits_or_latin_in_arabic',
  'slug_collision',
  'blank_arabic_text',
] as const;
export const importWarningCodeSchema = z.enum(IMPORT_WARNING_CODES);
export type ImportWarningCode = z.infer<typeof importWarningCodeSchema>;

export const importWarningSchema = z.object({
  code: importWarningCodeSchema,
  /** 1-based line numbers this warning applies to. */
  lines: z.array(z.number().int().min(1)),
  message: z.string(),
});
export type ImportWarning = z.infer<typeof importWarningSchema>;

export const previewRowSchema = z.object({
  rowIndex: z.number().int().min(0),
  line: z.number().int().min(1),
  englishText: z.string(),
  arabicText: z.string(),
  extraValues: extraValuesSchema,
});
export type PreviewRow = z.infer<typeof previewRowSchema>;

export const characterTotalsSchema = z.object({
  en: z.number().int().min(0),
  ar: z.number().int().min(0),
  total: z.number().int().min(0),
});
export type CharacterTotals = z.infer<typeof characterTotalsSchema>;

/** Credit estimate vs live remaining credits (SPEC §5/§6). */
export const creditEstimateSchema = z.object({
  estimatedCredits: z.number().int().min(0),
  remainingCredits: z.number().int().nullable(),
  /** null when the live subscription lookup failed — the UI shows "unknown". */
  sufficient: z.boolean().nullable(),
  assetsQueued: z.number().int().min(0),
  lookupError: z.string().nullable(),
});
export type CreditEstimate = z.infer<typeof creditEstimateSchema>;

/** POST /api/batches/import/preview (multipart: field `file`). */
export const importPreviewResponseSchema = z.object({
  previewId: idSchema,
  expiresAt: dateTimeSchema,
  sourceFilename: z.string(),
  detectedEncoding: detectedEncodingSchema,
  detectedDelimiter: csvDelimiterSchema,
  headers: z.array(z.string()),
  englishHeader: z.string(),
  arabicHeader: z.string(),
  passthroughHeaders: z.array(z.string()),
  rowCount: z.number().int().min(0),
  sampleRows: z.array(previewRowSchema),
  warnings: z.array(importWarningSchema),
  characterTotals: characterTotalsSchema,
  creditEstimate: creditEstimateSchema,
  suggestedName: z.string(),
});
export type ImportPreviewResponse = z.infer<typeof importPreviewResponseSchema>;

/** 422 body for a whole-file reject. */
export const importRejectedResponseSchema = z.object({
  code: z.literal('import_rejected'),
  message: z.string(),
  errors: z.array(importErrorSchema),
});
export type ImportRejectedResponse = z.infer<typeof importRejectedResponseSchema>;

/** POST /api/batches/import/commit */
export const importCommitRequestSchema = z.object({
  previewId: idSchema,
  name: z.string().trim().min(1).max(200),
});
export type ImportCommitRequest = z.infer<typeof importCommitRequestSchema>;

export const importCommitResponseSchema = z.object({
  batch: batchSummarySchema,
  rowsCreated: z.number().int().min(0),
  assetsCreated: z.number().int().min(0),
});
export type ImportCommitResponse = z.infer<typeof importCommitResponseSchema>;

/**
 * SPEC §7 — re-import into an existing batch (P2).
 * Ships as 501 until the rest of the app is green.
 */
export const reimportPreviewResponseSchema = z.object({
  previewId: idSchema,
  unchanged: z.number().int().min(0),
  updated: z.number().int().min(0),
  added: z.number().int().min(0),
  removed: z.number().int().min(0),
  warnings: z.array(importWarningSchema),
});
export type ReimportPreviewResponse = z.infer<typeof reimportPreviewResponseSchema>;

export const reimportCommitRequestSchema = z.object({ previewId: idSchema });
export type ReimportCommitRequest = z.infer<typeof reimportCommitRequestSchema>;
